import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  eInvoiceAuthTokenAction,
  eInvoiceInvoiceDataAction,
  eInvoiceGenerateAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import LoadingSpinner from "../../helper-components/loading-spinner";
import { ChevronDown, ChevronUp } from "lucide-react";

/* ─── session-storage key for auth token ────────────────────────────────────── */
const IRN_TOKEN_KEY = "auth_token";

/* ─── Dropdown styling ──────────────────────────────────────────────────────── */
const SELECT_CLASS =
  "border border-[var(--input-border)] rounded-[6px] px-3 bg-[var(--input-bg)] text-[var(--input-color)] font-[var(--input-font-weight)] focus:outline-none focus:ring-1 focus:ring-[var(--button-bg-color)]";

/* ─── shared layout constants (matches e-way bill) ──────────────────────────── */
const LABEL_CLASS =
  "text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] whitespace-nowrap";

/* ─── Read-only field item (vertical layout for grids) ──────────────────────── */
const FieldItem = ({ label, value }) => (
  <div className="flex flex-col gap-[4px]">
    <span className={LABEL_CLASS}>
      {label}
    </span>
    <span
      className="border border-[var(--input-border)] rounded-[6px] px-3 bg-[var(--input-bg)] text-[var(--input-color)] font-[var(--input-font-weight)]"
      style={{ minHeight: "36px", display: "flex", alignItems: "center", fontSize: "13px" }}
    >
      {value ?? <span className="text-[var(--placeholder-color)]">—</span>}
    </span>
  </div>
);

/* ─── Select dropdown field item ────────────────────────────────────────────── */
const SelectField = ({ label, value, options, onChange, name }) => (
  <div className="flex flex-col gap-[4px] relative">
    <span className={LABEL_CLASS}>{label}</span>
    <div className="relative w-full">
      <select
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        className={`${SELECT_CLASS} w-full appearance-none pr-8 cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed`}
        style={{ minHeight: "36px", fontSize: "13px" }}
      >
        <option value="" disabled>Select {label}</option>
        {options?.map((opt, i) => (
          <option key={i} value={opt?.value ?? opt?.code ?? opt}>
            {opt?.name ?? opt?.value ?? opt}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
        </svg>
      </div>
    </div>
  </div>
);

/* ─── Collapsible section ─────────────────────────────────────────────────────  */
const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-[var(--create-table-header-bg)] text-white"
      >
        <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
          {title}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="px-5 py-4 flex flex-col gap-0">{children}</div>}
    </section>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const CreateEInvoice = ({ invoiceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);

  /* ── State for dropdown selections ── */
  const [supplyTypeCode, setSupplyTypeCode] = useState("O"); // default Outward based on EInvoiceMastersData
  const [documentTypeCode, setDocumentTypeCode] = useState("INV"); // default Tax Invoice
  const [isServiceModes, setIsServiceModes] = useState({}); // tracking IsService for each row idx

  useEffect(() => {
    let resolved = 0;
    const tryDone = () => {
      resolved += 1;
      if (resolved >= 2) setLoading(false);
    };

    /* ── 1. Auth token (POST) ─────────────────────────────────────────────── */
    dispatch(
      eInvoiceAuthTokenAction(
        (data) => {
          const token =
            data?.auth_token ??
            data?.data?.auth_token ??
            data?.authToken ??
            data?.data?.authToken ??
            data?.token ??
            data?.data?.token ??
            null;
          if (token) sessionStorage.setItem(IRN_TOKEN_KEY, token);
          tryDone();
        },
        (err) => {
          errorToast(err?.data?.message ?? "Failed to fetch e-Invoice auth token");
          tryDone();
        }
      )
    );

    /* ── 2. Invoice data (GET) ────────────────────────────────────────────── */
    if (invoiceId && invoiceId !== "new") {
      dispatch(
        eInvoiceInvoiceDataAction(
          { soit_id: invoiceId },
          (data) => {
            setInvoiceData(data?.data ?? data);
            tryDone();
          },
          (err) => {
            errorToast(err?.data?.message ?? "Failed to fetch invoice data for e-Invoice");
            tryDone();
          }
        )
      );
    } else {
      tryDone();
    }
  }, [invoiceId]);

  if (loading) return <LoadingSpinner />;

  /* ─── flatten master data (API payload structure) ───────────────────────── */
  const orderData = invoiceData?.orderData ?? {};
  const sellerData = invoiceData?.sellerData ?? {};
  const buyerData = invoiceData?.BuyerData ?? {};
  const itemData = invoiceData?.itemData ?? [];
  const mastersData = invoiceData?.EInvoiceMastersData ?? {};

  /* Map to E-Invoice fields */
  const irn = orderData?.irn ?? "";
  
  // Handlers for dropdowns
  const handleDropdownChange = (name, value) => {
    if (name === "supplyType") setSupplyTypeCode(value);
    if (name === "documentType") setDocumentTypeCode(value);
  };

  const getStateCode = (stateString) => {
    if (!stateString) return "29";
    if (/^([0-9]{1,2})$/.test(String(stateString))) return String(stateString);
    const map = {
        "andaman and nicobar islands": "35", "andhra pradesh": "37", "andhra pradesh (new)": "37",
        "arunachal pradesh": "12", "assam": "18", "bihar": "10", "chandigarh": "04",
        "chhattisgarh": "22", "dadra and nagar haveli": "26", "daman and diu": "25",
        "delhi": "07", "goa": "30", "gujarat": "24", "gujrat": "24", "haryana": "06", 
        "himachal pradesh": "02", "jammu and kashmir": "01", "jharkhand": "20",
        "karnataka": "29", "kerala": "32", "lakshadweep islands": "31", "madhya pradesh": "23",
        "maharashtra": "27", "manipur": "14", "meghalaya": "17", "mizoram": "15",
        "nagaland": "13", "odisha": "21", "pondicherry": "34", "punjab": "03", "rajasthan": "08",
        "sikkim": "11", "tamil nadu": "33", "telangana": "36", "tripura": "16",
        "uttar pradesh": "09", "uttarakhand": "05", "west bengal": "19"
    };
    return map[String(stateString).toLowerCase()] || "29"; // fallback to 29 (Karnataka)
  };

  const handlePushToIRP = () => {
    const authToken = sessionStorage.getItem(IRN_TOKEN_KEY);
    if (!authToken) {
      errorToast("Auth token missing. Please refresh the page.");
      return;
    }

    const monthYear = orderData?.document_date 
      ? new Date(orderData.document_date).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }).replace('/', '-') 
      : "";

    const payload = {
        authToken,
        monthYear,
        Version: "1.01",
        soit_id: invoiceId,
        TranDtls: {
            TaxSch: "GST",
            SupTyp: "B2B",
            RegRev: "N",
            IgstOnIntra: "N"
        },
        DocDtls: {
            Typ: documentTypeCode || "INV",
            No: (orderData?.soit_custom_invoice_number || "").substring(0, 16),
            Dt: orderData?.document_date ? new Date(orderData.document_date).toLocaleDateString('en-GB') : ""
        },
        SellerDtls: {
            Gstin: sellerData?.company_gstn || "",
            LglNm: sellerData?.company || "",
            TrdNm: sellerData?.company || "",
            Addr1: sellerData?.company_address || "",
            // Addr2 omitted if empty to prevent minimum length 3 validation error
            Loc: sellerData?.company_city || "",
            Pin: Number(sellerData?.company_pincode) || 0,
            Stcd: getStateCode(sellerData?.company_state),
            ...(sellerData?.company_phone?.length >= 6 ? { Ph: sellerData.company_phone } : {}),
            ...(sellerData?.company_email ? { Em: sellerData.company_email } : {})
        },
        BuyerDtls: {
            Gstin: orderData?.soit_ship_to_address?.cmr_ship_gst || "",
            LglNm: `${buyerData?.cmr_first_name ?? ""} ${buyerData?.cmr_last_name ?? ""}`.trim() || "",
            TrdNm: `${buyerData?.cmr_first_name ?? ""} ${buyerData?.cmr_last_name ?? ""}`.trim() || "",
            Pos: getStateCode(buyerData?.cmr_state),
            Addr1: buyerData?.cmr_address || "",
            // Addr2 omitted if empty to prevent minimum length 3 validation error
            Loc: buyerData?.cmr_city || buyerData?.cmr_town || "",
            Pin: Number(buyerData?.cmr_pincode) || 0,
            Stcd: getStateCode(buyerData?.cmr_state),
            ...(buyerData?.cmr_phone_number?.length >= 6 ? { Ph: buyerData.cmr_phone_number } : {}),
            ...(buyerData?.cmr_email ? { Em: buyerData.cmr_email } : {})
        },
        ItemList: itemData.map((item, idx) => ({
            item_id: item?.item_id ?? "",
            SlNo: String(idx + 1),
            PrdDesc: item?.item_name ?? "",
            IsServc: isServiceModes[idx] ?? "N",
            HsnCd: item?.item_hsn ?? "",
            Qty: Number(item?.item_quantity) || 0,
            Unit: item?.item_uom || "",
            UnitPrice: Number(item?.item_unit_price) || 0,
            TotAmt: Number(item?.item_total_amount) || 0,
            Discount: 0,
            AssAmt: Number(item?.item_total_amount) || 0,
            GstRt: Number(item?.item_gst) || 0,
            IgstAmt: Number(item?.item_igst) || 0,
            CgstAmt: Number(item?.item_cgst) || 0,
            SgstAmt: Number(item?.item_sgst) || 0,
            TotItemVal: (Number(item?.item_total_amount) || 0) + (Number(item?.item_igst) || 0) + (Number(item?.item_cgst) || 0) + (Number(item?.item_sgst) || 0)
        })),
        ValDtls: {
            AssVal: itemData.reduce((acc, item) => acc + (Number(item?.item_total_amount) || 0), 0),
            CgstVal: itemData.reduce((acc, item) => acc + (Number(item?.item_cgst) || 0), 0),
            SgstVal: itemData.reduce((acc, item) => acc + (Number(item?.item_sgst) || 0), 0),
            IgstVal: itemData.reduce((acc, item) => acc + (Number(item?.item_igst) || 0), 0),
            TotInvVal: Number(orderData?.soit_total_amount) || 0
        }
    };

    setLoading(true);
    dispatch(
      eInvoiceGenerateAction(
        payload,
        (res) => {
          setLoading(false);
          successToast(res?.message ?? "e-Invoice Generated Successfully");
          navigate("/sales-invoice");
        },
        (err) => {
          setLoading(false);
          errorToast(err?.message ?? "Failed to generate e-Invoice");
        }
      )
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* ════ SCROLLABLE BODY ══════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">

        {/* ── TRANSACTION DETAILS (TOP) ────────────────────────────────────── */}
        <Section title="Transaction Details">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SelectField 
              label="Document Type Code" 
              name="documentType"
              value={documentTypeCode}
              options={mastersData?.docType ?? []}
              onChange={handleDropdownChange}
            />
            <FieldItem label="Document Number" value={orderData?.inv_no} />
            <FieldItem label="Document Date" value={orderData?.document_date ? new Date(orderData.document_date).toLocaleDateString("en-GB") : ""} />
            <FieldItem label="Total Invoice Value" value={orderData?.soit_total_amount} />
            {/* <FieldItem label="Preceding Invoice Reference and Date" value={""} /> */}
            <FieldItem label="IRN – Invoice Reference Number" value={orderData?.soit_custom_invoice_number} />
            <SelectField 
              label="Supply Type Code" 
              name="supplyType"
              value={supplyTypeCode}
              options={mastersData?.supplyType ?? []}
              onChange={handleDropdownChange}
            />
          </div>
        </Section>

        {/* ── SELLER & BUYER DETAILS (GRID) ──────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Organisation (Seller) Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldItem label="Supplier Legal Name" value={sellerData?.company} />
              <FieldItem label="Supplier GSTIN" value={sellerData?.company_gstn} />
              <FieldItem label="Supplier Address" value={sellerData?.company_address} />
              <FieldItem label="Supplier Place" value={sellerData?.company_city} />
              <FieldItem label="Supplier State Code" value={sellerData?.company_state} />
              <FieldItem label="Supplier Pincode" value={sellerData?.company_pincode} />
              <FieldItem label="Dispatch From – Name" value={sellerData?.company} />
              <FieldItem label="Dispatch From – Address" value={sellerData?.company_address} />
              <FieldItem label="Dispatch From – Place" value={sellerData?.company_city} />
              <FieldItem label="Dispatch From – Pincode" value={sellerData?.company_pincode} />
            </div>
          </Section>

          <Section title="Customer (Buyer) Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldItem label="Recipient Legal Name" value={`${buyerData?.cmr_first_name ?? ""} ${buyerData?.cmr_last_name ?? ""}`.trim()} />
              <FieldItem label="Recipient GSTIN" value={orderData?.soit_ship_to_address?.cmr_ship_gst} />
              <FieldItem label="Recipient Address" value={buyerData?.cmr_address} />
              <FieldItem label="Recipient State Code" value={buyerData?.cmr_state} />
              <FieldItem label="Place of Supply State Code" value={buyerData?.cmr_state} />
              <FieldItem label="Recipient Pincode" value={buyerData?.cmr_pincode} />
              <FieldItem label="Recipient Place" value={buyerData?.cmr_city || buyerData?.cmr_town} />
              <FieldItem label="ShippingTo GSTIN" value={orderData?.soit_ship_to_address?.cmr_ship_gst} />
              <FieldItem label="ShippingTo State" value={orderData?.soit_ship_to_address?.cmr_ship_address_state} />
              <FieldItem label="ShippingTo Pincode" value={orderData?.soit_ship_to_address?.cmr_ship_address_pincode} />
              <FieldItem label="ShippingTo State Code" value={orderData?.soit_ship_to_address?.cmr_ship_address_state} />
            </div>
          </Section>
        </div>

        {/* ── ITEM DETAILS ─────────────────────────────────────────────────── */}
        <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
          <div className="px-5 py-3 bg-[var(--create-table-header-bg)] text-white">
            <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
              Item Details
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" style={{ fontSize: "13px" }}>
              <thead>
                <tr className="bg-[var(--list-table-heading-bg)] text-[var(--list-table-heading-fg-color)]">
                  {["#", "Item Name", "HSN Code", "Total Amount", "GST Rate", "IGST", "CGST", "SGST", "Is Service"].map((h) => (
                    <th key={h} className="px-4 py-[10px] text-left font-[500] border-b border-[var(--light-border-color)] whitespace-nowrap" style={{ fontSize: "12px" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemData.length > 0 ? (
                  itemData.map((item, idx) => (
                    <tr key={idx} className="border-b border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]">
                      <td className="px-4 py-3 text-[var(--secondary)]">{idx + 1}</td>
                      <td className="px-4 py-3 text-[var(--secondary)] font-[500]">
                        {item?.item_name ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_hsn ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_total_amount ?? "—"}
                      </td>
                      {/* <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_sub_total ?? "—"}
                      </td> */}
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_gst ?? "—"}%
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_igst ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_cgst ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        {item?.item_sgst ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--secondary)]">
                        <select
                          className={`${SELECT_CLASS} w-[70px] cursor-pointer`}
                          style={{ minHeight: "30px", fontSize: "12px", padding: "0 8px" }}
                          value={isServiceModes[idx] ?? "N"}
                          onChange={(e) => setIsServiceModes(prev => ({ ...prev, [idx]: e.target.value }))}
                        >
                          <option value="N">No</option>
                          <option value="Y">Yes</option>
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="px-4 py-4 text-center text-[var(--placeholder-color)]">
                      No item data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* ════ STICKY FOOTER ═════════════════════════════════════════════════ */}
      <div
        className="border-t border-[var(--create-table-footer-border)] bg-white h-[4rem] flex justify-end items-center px-5 gap-3"
        style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.04)" }}
      >
        <button
          type="button"
          onClick={() => navigate("/sales-invoice")}
          className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
            bg-[var(--draft-button-bg)] cursor-pointer
            text-[var(--draft-button-fg)]
            border border-[var(--draft-button-border)]
            text-[length:var(--draft-button-fs)]
            font-[var(--draft-button-fw)]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePushToIRP}
           className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
              bg-[var(--primary-button-bg-color)] cursor-pointer
              text-[var(--primary-button-fg-color)]
              border border-[var(--primary-button-bg-color)]
              hover:bg-[var(--primary-button-hover-color)]
              text-[length:var(--primary-button-create-fs)]
              font-[var(--primary-button-font-weight)]
              disabled:opacity-60"
        >
          Push to IRP
        </button>
      </div>
    </div>
  );
};

export default CreateEInvoice;
